/**
 * LAB-04 — how the prescription sheet is laid out on paper.
 *
 * Doctors print on their own pre-printed letterhead as often as on plain paper,
 * so the header and footer are configurable: the clinic's generated letterhead,
 * an uploaded image, an empty reserved band that leaves the paper's own
 * letterhead clear, or nothing at all. Stored as one clinic-wide setting
 * (`print.prescription`); the API validates with `validatePrescriptionPrintLayout`
 * and every reader goes through `normalisePrescriptionPrintLayout`, so a key
 * added later never reaches a screen as `undefined`.
 */

export const RX_PAPER_SIZES = ['A4', 'A5'] as const;
export type RxPaperSize = (typeof RX_PAPER_SIZES)[number];

export const RX_ORIENTATIONS = ['portrait', 'landscape'] as const;
export type RxOrientation = (typeof RX_ORIENTATIONS)[number];

/** SYSTEM = the clinic letterhead built from the identity settings. */
export const RX_HEADER_MODES = ['SYSTEM', 'IMAGE', 'BLANK', 'NONE'] as const;
export type RxHeaderMode = (typeof RX_HEADER_MODES)[number];

/** BLANK reserves the band for a pre-printed footer; NONE gives it to the content. */
export const RX_FOOTER_MODES = ['IMAGE', 'BLANK', 'NONE'] as const;
export type RxFooterMode = (typeof RX_FOOTER_MODES)[number];

/** BOTH prints the English name with the Arabic one under it. */
export const RX_DRUG_NAME_LANGUAGES = ['AR', 'EN', 'BOTH'] as const;
export type RxDrugNameLanguage = (typeof RX_DRUG_NAME_LANGUAGES)[number];

export const RX_PRINT_COLUMNS = [
  'index',
  'medication',
  'dose',
  'frequency',
  'duration',
  'quantity',
  'route',
  'instructions',
] as const;
export type RxPrintColumn = (typeof RX_PRINT_COLUMNS)[number];

export const RX_PRINT_ELEMENTS = [
  'clinicHeader',
  'prescriptionNumber',
  'date',
  'patientName',
  'fileNumber',
  'age',
  'phone',
  'provider',
  'diagnosis',
  'allergies',
  'allergyOverride',
  'notes',
  'signature',
  'generatedAt',
] as const;
export type RxPrintElementKey = (typeof RX_PRINT_ELEMENTS)[number];

export const RX_ELEMENT_ANCHORS = ['PAGE', 'AFTER_TABLE'] as const;
export const RX_ELEMENT_ALIGNS = ['start', 'center', 'end'] as const;

export interface RxPrintElement {
  key: RxPrintElementKey;
  visible: boolean;
  showLabel: boolean;
  anchor: (typeof RX_ELEMENT_ANCHORS)[number];
  /** From the start side of the content box (right in Arabic). */
  xMm: number;
  /** From the content top on page one, or from the end of the drug table. */
  yMm: number;
  widthMm: number;
  align: (typeof RX_ELEMENT_ALIGNS)[number];
}

/** Paper dimensions in millimetres, portrait. */
export const RX_PAPER_MM: Record<RxPaperSize, { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
};

export const RX_LIMITS = {
  coordinateMm: { min: 0, max: 420 },
  widthMm: { min: 5, max: 420 },
  marginMm: { min: 0, max: 60 },
  bandMm: { min: 0, max: 120 },
  fontScalePercent: { min: 70, max: 150 },
} as const;

export interface RxPrintBand<Mode extends string> {
  mode: Mode;
  imageFileId: string | null;
  /** Height reserved on every page for IMAGE and BLANK. */
  heightMm: number;
}

export interface PrescriptionPrintLayout {
  paperSize: RxPaperSize;
  orientation: RxOrientation;
  marginsMm: { top: number; bottom: number; start: number; end: number };
  header: RxPrintBand<RxHeaderMode>;
  footer: RxPrintBand<RxFooterMode>;
  fontScalePercent: number;
  drugNameLanguage: RxDrugNameLanguage;
  /** Print order; hidden columns keep their place for when they are switched back on. */
  columns: { key: RxPrintColumn; visible: boolean }[];
  table: { xMm: number; yMm: number; widthMm: number; showHeaderRow: boolean };
  /** Settings order and paint order; hidden elements keep their place. */
  elements: RxPrintElement[];
}

const HIDDEN_BY_DEFAULT: readonly RxPrintColumn[] = ['quantity', 'route'];

/** Reproduces the sheet as it printed before the layout became configurable. */
export const DEFAULT_PRESCRIPTION_PRINT_LAYOUT: PrescriptionPrintLayout =
  autoArrangePrescriptionElements({
    paperSize: 'A4',
    orientation: 'portrait',
    marginsMm: { top: 10, bottom: 10, start: 10, end: 10 },
    header: { mode: 'SYSTEM', imageFileId: null, heightMm: 35 },
    footer: { mode: 'NONE', imageFileId: null, heightMm: 20 },
    fontScalePercent: 100,
    drugNameLanguage: 'AR',
    columns: RX_PRINT_COLUMNS.map((key) => ({ key, visible: !HIDDEN_BY_DEFAULT.includes(key) })),
    table: { xMm: 0, yMm: 0, widthMm: 190, showHeaderRow: true },
    elements: RX_PRINT_ELEMENTS.map((key) => ({
      key,
      visible: true,
      showLabel: true,
      anchor: 'PAGE',
      xMm: 0,
      yMm: 0,
      widthMm: 190,
      align: 'start',
    })),
  });

/** Arrange the current list without changing visibility, labels or its order. */
export function autoArrangePrescriptionElements(
  layout: PrescriptionPrintLayout,
): PrescriptionPrintLayout {
  const paper = RX_PAPER_MM[layout.paperSize];
  const width = Math.max(
    5,
    (layout.orientation === 'landscape' ? paper.height : paper.width) -
      layout.marginsMm.start -
      layout.marginsMm.end,
  );
  const slots = Math.max(1, Math.min(3, Math.floor(width / 40)));
  const gap = slots > 1 ? 4 : 0;
  const shortWidth = (width - gap * (slots - 1)) / slots;
  let y = 0;
  let slot = 0;
  let afterY = 3;
  let afterSlot = 0;
  const flush = () => {
    if (slot) {
      y += 13;
      slot = 0;
    }
  };
  const leadingHeader =
    layout.elements[0]?.key === 'clinicHeader' &&
    layout.elements[0].visible &&
    layout.header.mode === 'SYSTEM';
  const elements = layout.elements.map((element, index): RxPrintElement => {
    const next: RxPrintElement = {
      ...element,
      anchor: 'PAGE',
      xMm: 0,
      yMm: y,
      widthMm: width,
      align: 'start',
    };
    if (element.key === 'notes' || element.key === 'signature' || element.key === 'generatedAt') {
      next.anchor = 'AFTER_TABLE';
      next.yMm = afterY;
      if (element.key === 'notes') {
        if (afterSlot) {
          afterY += 23;
          afterSlot = 0;
          next.yMm = afterY;
        }
        if (element.visible) afterY += 13;
      } else {
        next.widthMm = (width - 4) / 2;
        next.xMm = afterSlot * (next.widthMm + 4);
        next.align = afterSlot ? 'end' : 'start';
        if (element.visible && ++afterSlot === 2) {
          afterSlot = 0;
          afterY += 23;
        }
      }
      return next;
    }
    // The usual letterhead has its number/date in the opposite corner.
    if (
      leadingHeader &&
      ((index === 1 && element.key === 'prescriptionNumber') ||
        (index === 2 && element.key === 'date'))
    ) {
      next.widthMm = Math.min(50, width / 3);
      next.xMm = width - next.widthMm;
      next.yMm = index === 1 ? 0 : 12;
      next.align = 'end';
      return next;
    }
    if (['clinicHeader', 'allergies', 'allergyOverride'].includes(element.key)) {
      flush();
      next.yMm = y;
      if (element.visible && (element.key !== 'clinicHeader' || layout.header.mode === 'SYSTEM')) {
        y += element.key === 'clinicHeader' ? 28 : element.key === 'allergies' ? 10 : 19;
      }
    } else {
      next.widthMm = shortWidth;
      next.xMm = slot * (shortWidth + gap);
      next.yMm = y;
      if (element.visible && ++slot === slots) {
        slot = 0;
        y += 13;
      }
    }
    return next;
  });
  flush();
  return { ...layout, elements, table: { ...layout.table, xMm: 0, yMm: y + 3, widthMm: width } };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const pick = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const clamp = (value: unknown, range: { min: number; max: number }, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(range.max, Math.max(range.min, value))
    : fallback;

const fileId = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

function normaliseBand<Mode extends string>(
  value: unknown,
  modes: readonly Mode[],
  fallback: RxPrintBand<Mode>,
): RxPrintBand<Mode> {
  const band = isRecord(value) ? value : {};
  return {
    mode: pick(modes, band.mode, fallback.mode),
    imageFileId: fileId(band.imageFileId),
    heightMm: clamp(band.heightMm, RX_LIMITS.bandMm, fallback.heightMm),
  };
}

/** Any stored shape → a complete layout; unknown or out-of-range parts fall back to the default. */
export function normalisePrescriptionPrintLayout(value: unknown): PrescriptionPrintLayout {
  const d = DEFAULT_PRESCRIPTION_PRINT_LAYOUT;
  const raw = isRecord(value) ? value : {};
  const margins = isRecord(raw.marginsMm) ? raw.marginsMm : {};
  const fields = isRecord(raw.fields) ? raw.fields : {};

  const seen = new Set<RxPrintColumn>();
  const columns: PrescriptionPrintLayout['columns'] = [];
  for (const entry of Array.isArray(raw.columns) ? raw.columns : []) {
    if (!isRecord(entry)) continue;
    const key = entry.key as RxPrintColumn;
    if (!RX_PRINT_COLUMNS.includes(key) || seen.has(key)) continue;
    seen.add(key);
    columns.push({ key, visible: entry.visible !== false });
  }
  // A column added after the clinic saved its layout joins at the end, as the default has it.
  for (const column of d.columns) if (!seen.has(column.key)) columns.push({ ...column });

  const base: PrescriptionPrintLayout = {
    paperSize: pick(RX_PAPER_SIZES, raw.paperSize, d.paperSize),
    orientation: pick(RX_ORIENTATIONS, raw.orientation, d.orientation),
    marginsMm: {
      top: clamp(margins.top, RX_LIMITS.marginMm, d.marginsMm.top),
      bottom: clamp(margins.bottom, RX_LIMITS.marginMm, d.marginsMm.bottom),
      start: clamp(margins.start, RX_LIMITS.marginMm, d.marginsMm.start),
      end: clamp(margins.end, RX_LIMITS.marginMm, d.marginsMm.end),
    },
    header: normaliseBand(raw.header, RX_HEADER_MODES, d.header),
    footer: normaliseBand(raw.footer, RX_FOOTER_MODES, d.footer),
    fontScalePercent: clamp(raw.fontScalePercent, RX_LIMITS.fontScalePercent, d.fontScalePercent),
    drugNameLanguage: pick(RX_DRUG_NAME_LANGUAGES, raw.drugNameLanguage, d.drugNameLanguage),
    columns,
    elements: d.elements.map((element) => ({
      ...element,
      visible:
        !Array.isArray(raw.elements) && typeof fields[element.key] === 'boolean'
          ? (fields[element.key] as boolean)
          : element.visible,
    })),
    table: { ...d.table },
  };
  const arranged = autoArrangePrescriptionElements(base);
  const seenElements = new Set<RxPrintElementKey>();
  const elements: RxPrintElement[] = [];
  for (const entry of Array.isArray(raw.elements) ? raw.elements : []) {
    if (!isRecord(entry)) continue;
    const key = entry.key as RxPrintElementKey;
    if (!RX_PRINT_ELEMENTS.includes(key) || seenElements.has(key)) continue;
    seenElements.add(key);
    const fallback = arranged.elements.find((element) => element.key === key)!;
    elements.push({
      key,
      visible: typeof entry.visible === 'boolean' ? entry.visible : fallback.visible,
      showLabel: typeof entry.showLabel === 'boolean' ? entry.showLabel : true,
      anchor: pick(RX_ELEMENT_ANCHORS, entry.anchor, fallback.anchor),
      align: pick(RX_ELEMENT_ALIGNS, entry.align, fallback.align),
      xMm: clamp(entry.xMm, RX_LIMITS.coordinateMm, fallback.xMm),
      yMm: clamp(entry.yMm, RX_LIMITS.coordinateMm, fallback.yMm),
      widthMm: clamp(entry.widthMm, RX_LIMITS.widthMm, fallback.widthMm),
    });
  }
  for (const element of arranged.elements) {
    if (!seenElements.has(element.key)) elements.push({ ...element });
  }
  const table = isRecord(raw.table) ? raw.table : {};
  return {
    ...base,
    elements,
    table: {
      xMm: clamp(table.xMm, RX_LIMITS.coordinateMm, arranged.table.xMm),
      yMm: clamp(table.yMm, RX_LIMITS.coordinateMm, arranged.table.yMm),
      widthMm: clamp(table.widthMm, RX_LIMITS.widthMm, arranged.table.widthMm),
      showHeaderRow: typeof table.showHeaderRow === 'boolean' ? table.showHeaderRow : true,
    },
  };
}

/**
 * Strict check for a write: the normaliser forgives a stored value, but a save
 * that would be silently rewritten is refused instead. Returns the offending
 * path, or null when the value is a complete, valid layout.
 */
export function validatePrescriptionPrintLayout(value: unknown): string | null {
  if (!isRecord(value)) return 'layout';
  if (!RX_PAPER_SIZES.includes(value.paperSize as RxPaperSize)) return 'paperSize';
  if (!RX_ORIENTATIONS.includes(value.orientation as RxOrientation)) return 'orientation';
  if (!RX_DRUG_NAME_LANGUAGES.includes(value.drugNameLanguage as RxDrugNameLanguage)) {
    return 'drugNameLanguage';
  }

  const inRange = (n: unknown, range: { min: number; max: number }) =>
    typeof n === 'number' && Number.isFinite(n) && n >= range.min && n <= range.max;

  if (!inRange(value.fontScalePercent, RX_LIMITS.fontScalePercent)) return 'fontScalePercent';
  if (!isRecord(value.marginsMm)) return 'marginsMm';
  for (const side of ['top', 'bottom', 'start', 'end'] as const) {
    if (!inRange(value.marginsMm[side], RX_LIMITS.marginMm)) return `marginsMm.${side}`;
  }

  const bands = [
    ['header', RX_HEADER_MODES],
    ['footer', RX_FOOTER_MODES],
  ] as const;
  for (const [name, modes] of bands) {
    const band = value[name];
    if (!isRecord(band)) return name;
    if (!(modes as readonly string[]).includes(band.mode as string)) return `${name}.mode`;
    if (!inRange(band.heightMm, RX_LIMITS.bandMm)) return `${name}.heightMm`;
    if (
      band.imageFileId !== null &&
      (typeof band.imageFileId !== 'string' || band.imageFileId.length > 64)
    ) {
      return `${name}.imageFileId`;
    }
    if (band.mode === 'IMAGE' && !band.imageFileId) return `${name}.imageFileId`;
  }

  if (!Array.isArray(value.columns) || value.columns.length !== RX_PRINT_COLUMNS.length)
    return 'columns';
  const keys = new Set<string>();
  for (const column of value.columns) {
    if (!isRecord(column) || typeof column.visible !== 'boolean') return 'columns';
    if (!RX_PRINT_COLUMNS.includes(column.key as RxPrintColumn) || keys.has(column.key as string)) {
      return 'columns';
    }
    keys.add(column.key as string);
  }

  if (!isRecord(value.table)) return 'table';
  for (const axis of ['xMm', 'yMm', 'widthMm'] as const) {
    if (
      !inRange(value.table[axis], axis === 'widthMm' ? RX_LIMITS.widthMm : RX_LIMITS.coordinateMm)
    ) {
      return `table.${axis}`;
    }
  }
  if (typeof value.table.showHeaderRow !== 'boolean') return 'table.showHeaderRow';
  if (!Array.isArray(value.elements) || value.elements.length !== RX_PRINT_ELEMENTS.length)
    return 'elements';
  const elementKeys = new Set<string>();
  for (const [index, element] of value.elements.entries()) {
    const path = `elements.${index}`;
    if (!isRecord(element)) return path;
    if (
      !RX_PRINT_ELEMENTS.includes(element.key as RxPrintElementKey) ||
      elementKeys.has(element.key as string)
    ) {
      return `${path}.key`;
    }
    elementKeys.add(element.key as string);
    for (const flag of ['visible', 'showLabel'] as const) {
      if (typeof element[flag] !== 'boolean') return `${path}.${flag}`;
    }
    if (!RX_ELEMENT_ANCHORS.includes(element.anchor as RxPrintElement['anchor']))
      return `${path}.anchor`;
    if (!RX_ELEMENT_ALIGNS.includes(element.align as RxPrintElement['align']))
      return `${path}.align`;
    for (const axis of ['xMm', 'yMm', 'widthMm'] as const) {
      if (
        !inRange(element[axis], axis === 'widthMm' ? RX_LIMITS.widthMm : RX_LIMITS.coordinateMm)
      ) {
        return `${path}.${axis}`;
      }
    }
  }
  return null;
}
